import { ChangeEvent } from "react";
import { DateTime } from "luxon";

import { Client } from "@/Common/Api/Client";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { RadioButtonv2 } from "@/Common/Components/Inputs/RadioButtonv2";
import { BaseSettingsContainer, BaseSettingsContainerProps, BaseSettingsContainerState } from "@/Common/Components/Settings/BaseSettingsContainer";
import { SpanControl } from "@/Common/Components/SpanControl";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { PreventiveCareActivitiesHelper } from "../Helpers/PreventiveCareActivitiesHelper";
import { PreventiveRecallListSearchSettings, SearchSettingsDatesOptionsEnum } from "../Models/Settings/PreventiveRecallListSearchSettings";
import { IPreventiveRecallListSettingsFooterProps, PreventiveRecallListSettingsFooter } from "./PreventiveRecallListSettingsFooter";

import styles from "./PreventiveRecallListSettingsContainer.module.scss";

interface IPreventiveRecallListSettingsContainerProps extends BaseSettingsContainerProps {
    onOkCallback?: (model: PreventiveRecallListSettingsContainerState) => void;
    onViewCriteria?: () => void;
    savedState: PreventiveRecallListSettingsContainerState;
}

interface ISearchSettingsPopupHeaderProps {
    node: React.ReactNode;
    id: string;
}

class PreventiveRecallListSettingsContainerState extends BaseSettingsContainerState {
    public globalProvidersList: EmployeeUIModel[];
    public showAllProviders: boolean;
    public isSaveSettingsChecked: boolean;
    public includeNonEnrolledPatients: boolean;
    public selectedDateOption: SearchSettingsDatesOptionsEnum;
    public showAllSites: boolean;
    public providers: EmployeeUIModel[];
    public dateFrom: Date;
    public dateTo: Date;
    public providersSearchText: string;
    public sites: SiteUIModel[];
    public PreventiveCareCriteriaId: string;
    public pageSize: number;
    public isOkButtonDisabled?: boolean;
    public coverageDateValidationError?: string;

    public constructor(fields?: Partial<PreventiveRecallListSettingsContainerState>) {
        super();
        Object.assign(this, fields);
    }

    public static getDefault(): PreventiveRecallListSettingsContainerState {
        const state = new PreventiveRecallListSettingsContainerState({
            showAllProviders: false,
            includeNonEnrolledPatients: false,
            selectedDateOption: SearchSettingsDatesOptionsEnum.EndOfFiscalYear,
            showAllSites: false,
            dateFrom: PreventiveCareActivitiesHelper.getFiscalYearStart(),
            dateTo: PreventiveCareActivitiesHelper.getFiscalYearEnd(),
            providersSearchText: "",
            globalProvidersList: cacheStore
                .GetCliniciansAsEmployees(CheckboxTarget.Unassigned, true)
                .mutate(clinician => clinician.Checked = (clinician.ContactFacetId === sessionStore.getCurrentEmployee().ContactFacetId)),
            pageSize: cacheStore.getDefaultPageSize(),
            isSaveSettingsChecked: false,
            sites: (cacheStore.SiteCache ?? [])
                .map(site => {
                    const siteUi = SiteUIModel.FromCompositeObject(site);
                    siteUi.Checked = true;
                    return siteUi;
                }),
            PreventiveCareCriteriaId: cacheStore.PreventiveCareCriteriaList?.firstOrDefault()?.Id
        });

        state.providers = state.globalProvidersList;
        PreventiveRecallListSettingsContainerState.setSearchSettingsFromUserPreferences(state);
        return state;
    }

    private static setSearchSettingsFromUserPreferences(state: PreventiveRecallListSettingsContainerState): void {
        const settings = cacheStore.UserSettings?.PreventiveRecallListSearchSettings;
        if (!settings)
            return;

        state.showAllProviders = settings.ShowAllProviders || state.showAllProviders;
        state.showAllSites = settings.ShowAllSites || state.showAllSites;
        state.dateFrom = settings.DateFrom || state.dateFrom;
        state.dateTo = settings.DateTo || state.dateTo;
        state.includeNonEnrolledPatients = settings.IncludeNonEnrolledPatients || state.includeNonEnrolledPatients;
        state.selectedDateOption = settings.SelectedDateOption || state.selectedDateOption;
        state.PreventiveCareCriteriaId = settings.PreventiveCareCriteriaId || state.PreventiveCareCriteriaId;

        if (settings.ProviderIds) {
            state.providers.mutate(c => c.Checked = settings.ProviderIds.contains(c.ContactFacetId));
            state.globalProvidersList.mutate(p => p.Checked = settings.ProviderIds.contains(p.ContactFacetId));
        }

        if (settings.SiteIds)
            state.sites.mutate(s => s.Checked = settings.SiteIds.contains(s.Id));
    }
}

class PreventiveRecallListSettingsContainer extends BaseSettingsContainer<IPreventiveRecallListSettingsContainerProps, PreventiveRecallListSettingsContainerState> {
    public constructor(props: IPreventiveRecallListSettingsContainerProps) {
        super(props);
        this.state = {
            ...PreventiveRecallListSettingsContainerState.getDefault(),
            isOkButtonDisabled: false
        };
    }

    public componentDidMount(): void {
        if (this.props.savedState) {
            this.setState({
                ...this.props.savedState,
                isSaveSettingsChecked: false
            });
        }
    }

    public render(): JSX.Element {
        return (
            <div className={`${this.props.className} filter-component-responsive`}>
                <table className="f-content">
                    <thead>
                        <tr>
                            {this.getHeaders().map(header => <th key={header.id}>{header.node}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {this.getColumnData().map(data => <td key={data.id}>{data.node}</td>)}
                        </tr>
                    </tbody>
                </table>
                <PreventiveRecallListSettingsFooter {...this.getSearchSettingsFooterProps()} />
            </div>
        );
    }

    protected getHeaders(): ISearchSettingsPopupHeaderProps[] {
        return [
            {
                id: "dueDates",
                node: (
                    <SpanControl className="push-left-5" text={cacheStore.GetString("DatesLabel").toUpperCase()} />
                )
            },
            {
                id: "allSites",
                node: this.state.sites.length > 1 && (
                    <CheckboxControl
                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                        checked={this.state.showAllSites}
                        onChange={checked => {
                            this.mutateState(
                                state => {
                                    state.showAllSites = checked;
                                    state.sites.forEach(p => p.Checked = checked);
                                },
                                () => this.updateEmployeeList()
                            );
                        }}
                    />
                )
            },
            {
                id: "allClinicians",
                node: <CheckboxControl
                    label={cacheStore.GetString("RecallListSettings_ProvidersLabel").toUpperCase()}
                    checked={this.state.showAllProviders}
                    onChange={checked => this.mutateState(
                        state => {
                            state.showAllProviders = checked;
                            state.globalProvidersList.forEach(p => p.Checked = checked);
                            state.providers.forEach(p => p.Checked = checked);
                        }
                    )}
                />
            },
            {
                id: "allItems",
                node: cacheStore.GetString("ItemsDLabel").toUpperCase()
            },
            {
                id: "settings",
                node: cacheStore.GetString("SettingsLabel").toUpperCase()
            }
        ]
            .filter(p => !!p.node);
    }

    protected getColumnData(): ISearchSettingsPopupHeaderProps[] {
        const dateSelectorDisabledOptions = [SearchSettingsDatesOptionsEnum.AsOfToday, SearchSettingsDatesOptionsEnum.EndOfFiscalYear];
        const isDateSelectorDisabled = dateSelectorDisabledOptions.includes(this.state.selectedDateOption);

        return [
            {
                id: "dueDateSelection",
                node: (
                    <div className={styles.dateOptionsWrapper}>
                        <RadioButtonv2
                            htmlName="dateOptions"
                            items={[
                                new KeyValuePair(SearchSettingsDatesOptionsEnum.EndOfFiscalYear, cacheStore.GetString("RecallListSettings_ByEndOfFiscalYearLabel")),
                                new KeyValuePair(SearchSettingsDatesOptionsEnum.AsOfToday, cacheStore.GetString("RecallListSettings_AsOfTodayLabel")),
                                new KeyValuePair(SearchSettingsDatesOptionsEnum.Personalized, cacheStore.GetString("RecallListSettings_PersonalizedLabel"))
                            ].toImmutable()}
                            selectedItem={this.state.selectedDateOption}
                            onChangeCallback={(selectedItem: KeyValuePair<SearchSettingsDatesOptionsEnum, string>) => this.onChangeDateOptions(selectedItem)}
                        />
                        <MultiDateSelector
                            key={`coverage_date_selector_${this.state.selectedDateOption}`}
                            cssClass={isDateSelectorDisabled ? styles.dateFieldDisabled : ""}
                            id="coverage_multi_date_selector"
                            fromChange={date => this.mutateState(state => state.dateFrom = date && DateTime.fromJSDate(date).startOf("day").toJSDate(), this.validateDates)}
                            toChange={date => this.mutateState(state => state.dateTo = date && DateTime.fromJSDate(date).endOf("day").toJSDate(), this.validateDates)}
                            from={this.state.dateFrom}
                            to={this.state.dateTo}
                            fromSetTodayLabel=""
                            toSetTodayLabel=""
                            errorMessage={this.state.coverageDateValidationError}
                        />
                    </div>
                )
            },
            {
                id: "sitesSelection",
                node: this.state.sites.length > 1 && (
                    <div className="sites-column">
                        <div className={styles.sitesControl}>
                            <CheckedListControl
                                DataSource={this.state.sites.toImmutable()}
                                onChange={(checked, id) => this.mutateState(
                                    state => state.sites.firstOrDefault(p => p.Id === id).Checked = checked,
                                    () => this.updateEmployeeList()
                                )}
                                MaxTextLength={25}
                            />
                        </div>
                    </div>
                )
            },
            {
                id: "providersSelect",
                node: (
                    <div>
                        <input
                            className={`${styles.searchText} form-control search-text`}
                            value={this.state.providersSearchText}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const searchText = e.target.value;
                                this.mutateState(
                                    state => state.providersSearchText = searchText,
                                    () => this.updateEmployeeList()
                                );
                            }}
                            placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                        />
                        <div className={styles.providerListControl}>
                            <CheckedListControl
                                idModifier="providersList"
                                DataSource={this.state.providers.map(p => ({ ...p, Id: p.ContactFacetId })).toImmutable()}
                                onChange={(checked, id) =>
                                    this.mutateState(state => {
                                        state.providers.firstOrDefault(p => p.ContactFacetId === id).Checked = checked;
                                        state.globalProvidersList.firstOrDefault(p => p.ContactFacetId === id).Checked = checked;
                                    })
                                }
                                sort
                                limitTextWidth
                            />
                        </div>
                    </div>
                )
            },
            {
                id: "itemsSelect",
                node: (
                    <div className={styles.itemsControls}>
                        <RadioButtonv2
                            htmlName="itemsRadioControl"
                            items={cacheStore.PreventiveCareCriteriaList.map(p => new KeyValuePair(p.Id, p.getTranslatedName())).toImmutable()}
                            selectedItem={this.state.PreventiveCareCriteriaId}
                            onChangeCallback={selectedItem => this.mutateState(state => state.PreventiveCareCriteriaId = selectedItem.Key)}
                        />
                    </div>
                )
            },
            {
                id: "settings",
                node: (
                    <div className={styles.settingsControls}>
                        <CheckboxControl
                            checked={this.state.includeNonEnrolledPatients}
                            label={cacheStore.GetString("RecallListSettings_IncludeNonEnrolledPatientLabel")}
                            onChange={checked => this.mutateState(state => state.includeNonEnrolledPatients = checked)}
                        />
                    </div>
                )
            }
        ]
            .filter(p => !!p.node);
    }

    protected getSearchSettingsFooterProps(): IPreventiveRecallListSettingsFooterProps {
        return {
            onClose: () => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SearchSettings_Container_Visibility, { isOpen: false }),
            onOk: () => this.onOkSettings(),
            onViewCriteria: () => this.props.onViewCriteria?.(),
            onColumnOrderingButtonClick: () => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SearchSettings_ColumnOrderingPopup_Visibility, { isOpen: true }),
            onResultsPerPageChange: value => this.mutateState(state => state.pageSize = value),
            selectedPageSizeOption: this.state.pageSize,
            isSaveSettingsChecked: this.state.isSaveSettingsChecked,
            onSaveSettingsCheckboxChange: checked => this.mutateState(state => state.isSaveSettingsChecked = checked),
            footerClassName: this.props.footerClassName,
            disableOkButton: this.state.isOkButtonDisabled
        };
    }

    protected shouldTriggerOkActionOnOutsideClick(): boolean {
        return !this.state.isOkButtonDisabled;
    }

    private updateEmployeeList(): void {
        this.mutateState(
            state => {
                const siteIds = this.state.sites.filter(site => site.Checked).map(p => p.Id);
                const filteredClinicians = state.globalProvidersList.filter(employee => employee.Sites.some(employeeSiteId => siteIds.contains(employeeSiteId)));

                state.providers = this.state.providersSearchText?.trim()
                    ? SearchMenuHelper.FilterCliniciansByName(this.state.providersSearchText?.trim(), filteredClinicians.toImmutable()).toArray()
                    : filteredClinicians;
            }
        );
    }

    private onChangeDateOptions(dateOption: KeyValuePair<SearchSettingsDatesOptionsEnum, string>): void {
        switch (dateOption.Key) {
            case SearchSettingsDatesOptionsEnum.EndOfFiscalYear:
                this.mutateState(state => {
                    state.selectedDateOption = SearchSettingsDatesOptionsEnum.EndOfFiscalYear;
                    state.dateFrom = PreventiveCareActivitiesHelper.getFiscalYearStart();
                    state.dateTo = PreventiveCareActivitiesHelper.getFiscalYearEnd();
                    state.coverageDateValidationError = "";
                    state.isOkButtonDisabled = false;
                });
                break;
            case SearchSettingsDatesOptionsEnum.AsOfToday:
                this.mutateState(state => {
                    state.selectedDateOption = SearchSettingsDatesOptionsEnum.AsOfToday;
                    state.dateFrom = PreventiveCareActivitiesHelper.getFiscalYearStart();
                    state.dateTo = DateTime.now().endOf("day").toJSDate();
                    state.coverageDateValidationError = "";
                    state.isOkButtonDisabled = false;
                });
                break;
            case SearchSettingsDatesOptionsEnum.Personalized:
                this.mutateState(state => {
                    state.selectedDateOption = SearchSettingsDatesOptionsEnum.Personalized;
                    state.dateFrom = null;
                    state.dateTo = null;
                    state.isOkButtonDisabled = true;
                });
                break;
        }
    }

    private onOkSettings(): void {
        if (this.state.isSaveSettingsChecked)
            this.saveUserSetting();

        this.props.onOkCallback?.(this.state);
    }

    private async saveUserSetting(): Promise<void> {
        const userSettings = new UserSettings({
            PreventiveRecallListSearchSettings: PreventiveRecallListSearchSettings.fromModel(this.state)
        });

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        if (response.isSuccess)
            cacheStore.UserSettings.PreventiveRecallListSearchSettings = userSettings.PreventiveRecallListSearchSettings;
    }

    private validateDates(): void {
        const dateFrom = cacheStore.Helper.CreateDateAsUTC(this.state.dateFrom);
        const dateTo = cacheStore.Helper.CreateDateAsUTC(this.state.dateTo);
        const shouldDisableOkButton = !dateFrom || !dateTo || dateFrom > dateTo;

        this.mutateState(state => {
            state.isOkButtonDisabled = shouldDisableOkButton;
            state.coverageDateValidationError = this.getCoverageDateValidationMessage(dateFrom, dateTo);
        });
    }

    private getCoverageDateValidationMessage(dateFrom: Date, dateTo: Date): string {
        if (dateFrom && dateTo && (dateFrom > dateTo))
            return cacheStore.GetString("StartDateCannotBeGreaterThanEndDateLabel");

        if (!dateFrom && !dateTo)
            return cacheStore.GetString("StartAndEndDatesAreRequiredLabel");

        if (!dateFrom)
            return cacheStore.GetString("StartDateIsRequiredLabel");

        if (!dateTo)
            return cacheStore.GetString("EndDateIsRequiredLabel");

        return "";
    }
}

export { PreventiveRecallListSettingsContainer, PreventiveRecallListSettingsContainerState };
